import { cn } from '@/shared/lib/cn';

/**
 * Colored chip for `Status_RO` values on `01.ResumenOperaciones`.
 *
 * The palette matches the PowerApps legend the business already knows —
 * distinct pastel tones per status so nine values stay distinguishable at
 * a glance. Labels are abbreviated for long statuses so the column stays
 * compact; the raw `Status_RO` value remains what's stored in SharePoint.
 */
type Tone =
  | 'green'
  | 'green-dark'
  | 'mint'
  | 'teal'
  | 'amber'
  | 'orange'
  | 'red'
  | 'rose'
  | 'violet'
  | 'blue'
  | 'fuchsia'
  | 'slate';

const STATUS_TONES: Record<string, Tone> = {
  Cotizado: 'green',
  Aceptado: 'blue',
  'Pendiente de Pago': 'red',
  // PowerApps now displays `Confirmacion de Pago` with the same label and
  // tone as `Pendiente de Pago` even though the backing SharePoint value
  // stays distinct. Mirror that here.
  'Confirmacion de Pago': 'red',
  'Pago Parcial': 'orange',
  'Pago Realizado': 'green-dark',
  'Pendiente Despacho': 'amber',
  Despachado: 'teal',
  'Pendiente Puesta Marcha': 'violet',
  Cerrado: 'slate',
  'Pendiente Aprobacion': 'fuchsia',
  Anulado: 'rose',
};

const SHORT_LABELS: Record<string, string> = {
  'Pendiente Puesta Marcha': 'PEND. P. EN MARCHA',
  'Confirmacion de Pago': 'PEND. DE PAGO',
  'Pendiente de Pago': 'PEND. DE PAGO',
  'Pendiente Despacho': 'PEND. DESPACHO',
  'Pendiente Aprobacion': 'PEND. APROBACIÓN',
};

const TONE_CLASSES: Record<Tone, string> = {
  green: 'bg-emerald-100 text-emerald-700',
  'green-dark': 'bg-emerald-200 text-emerald-800',
  mint: 'bg-[hsl(var(--brand-green))]/12 text-[hsl(var(--brand-green))]',
  teal: 'bg-teal-100 text-teal-700',
  amber: 'bg-amber-100 text-amber-700',
  orange: 'bg-orange-100 text-orange-700',
  red: 'bg-rose-100 text-rose-700',
  rose: 'bg-red-100 text-red-700',
  violet: 'bg-violet-100 text-violet-700',
  blue: 'bg-sky-100 text-sky-700',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-700',
  slate: 'bg-slate-100 text-slate-700',
};

export function StatusRoBadge({
  status,
  className,
}: {
  status: string | null | undefined;
  className?: string;
}) {
  const raw = status || '—';
  const label = SHORT_LABELS[raw] ?? raw.toUpperCase();
  const tone = STATUS_TONES[raw] ?? 'slate';
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-md px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {label}
    </span>
  );
}

/**
 * Predicate shared by the gallery and the detail screen: once a cotización
 * is accepted or moved into the payments/dispatch pipeline, the whole row
 * becomes display-only — no more Aceptar / Anular actions from the detail.
 */
export const READ_ONLY_STATUSES = [
  'Aceptado',
  'Confirmacion de Pago',
  'Pago Parcial',
  'Pago Realizado',
  'Pendiente Despacho',
  'Despachado',
  'Pendiente Puesta Marcha',
  'Cerrado',
  'Anulado',
] as const;

export function isVentaReadOnly(
  status: string | null | undefined,
  aceptado: string | null | undefined,
): boolean {
  if (aceptado === 'SI') return true;
  if (!status) return false;
  return (READ_ONLY_STATUSES as readonly string[]).includes(status);
}

/**
 * Statuses exposed in the filter dropdown, in display order.
 *
 * `Confirmacion de Pago` is intentionally absent: the UI labels it the same
 * as `Pendiente de Pago`, so showing both would produce duplicate entries.
 * The Ventas filter expands `Pendiente de Pago` to match both backing
 * SharePoint values — see `expandStatusFilter` in `VentasPage.tsx`.
 */
export const STATUS_RO_VALUES = [
  'Cotizado',
  'Aceptado',
  'Pendiente de Pago',
  'Pago Parcial',
  'Pago Realizado',
  'Pendiente Despacho',
  'Despachado',
  'Pendiente Puesta Marcha',
  'Cerrado',
  'Pendiente Aprobacion',
  'Anulado',
] as const;

export type StatusRo = (typeof STATUS_RO_VALUES)[number];
